import type { PushEventType, PushMessage, PushState as PushPluginState } from '@/plugins/uni-push';
import { defineStore } from 'pinia';

// 推送类型统一定义在通用插件中，这里重导出保持 `@/store` 原有引用可用
export type { PushInitStatus, PushMessage, PushPayloadData } from '@/plugins/uni-push';
export { PushEventType } from '@/plugins/uni-push';

// 推送 store 状态（运行时状态由插件通过 syncPluginState 同步进来）
export interface PushStoreState {
  clientId: string // 推送设备ID
  unreadCount: number // 未读消息数（扩展用）
  isListening: boolean // 是否已注册监听
  lastMessage: PushMessage | null // 最后一条消息（扩展用）
  initStatus: PushPluginState['initStatus'] // 初始化状态（运行时判断 uni-push 是否可用）
  initError: string // 初始化失败原因
  pushSoundEnabled: boolean // App 内设置：推送提示音总开关（消息通知设置页维护）
  pushVibrateEnabled: boolean // App 内设置：推送震动总开关（消息通知设置页维护）
}

export const usePushStore = defineStore({
  id: 'push',
  state: (): PushStoreState => ({
    clientId: '', // 推送设备ID
    unreadCount: 0, // 全局未读消息数（如聊天、系统通知）
    isListening: false, // 是否已注册推送监听
    lastMessage: null, // 最后一条接收/点击的消息
    initStatus: 'idle', // 初始化状态
    initError: '', // 初始化失败原因
    pushSoundEnabled: true, // 推送提示音总开关（默认开启）
    pushVibrateEnabled: true, // 推送震动总开关（默认开启）
  }),
  getters: {
    // 是否已获取有效ClientId
    hasValidClientId(): boolean {
      return !!this.clientId;
    },
    // uni-push 是否已启用且完成初始化（cid 获取成功 + 监听已注册）
    isPushReady(): boolean {
      return this.initStatus === 'success' && !!this.clientId && this.isListening;
    },
  },
  actions: {
    /**
     * 同步插件运行时状态（由 push.config.ts 的 onStateChange 回调触发）
     */
    syncPluginState(state: PushPluginState) {
      this.clientId = state.clientId;
      this.initStatus = state.initStatus;
      this.initError = state.initError;
      this.isListening = state.isListening;
    },

    /**
     * 记录最后一条消息
     */
    setLastMessage(msg: PushMessage, type: PushEventType) {
      this.lastMessage = { ...msg, _eventType: type }; // 附加事件类型
    },

    /**
     * 更新未读消息数
     */
    updateUnreadCount(count: number, type?: string) {
      if (type) {
        // 扩展：按类型更新（如仅更新chat类型）
        this.unreadCount += count;
      }
      else {
        this.unreadCount = count;
      }
      // 同步更新角标（APP端）
      this.updateBadge();
    },

    /**
     * 更新APP角标数（仅APP端）
     */
    updateBadge() {
      // #ifdef APP-PLUS
      plus.runtime.setBadgeNumber(this.unreadCount);
      // #endif
    },

    /**
     * 清空未读数（如进入聊天页面）
     */
    clearUnreadCount(type?: string) {
      if (type) {
        // 扩展：清空指定类型
      }
      else {
        this.unreadCount = 0;
        this.updateBadge();
      }
    },

  },
  persist: {
    paths: ['clientId', 'unreadCount', 'pushSoundEnabled', 'pushVibrateEnabled'], // 持久化核心状态与提醒开关
  },
});
